import * as Sentry from '@sentry/react';

// Sentry for the AssetSolutionsUS web frontend — a separate project from the API
// (org `secretsfl`, project `assetsolutionsus_frontend`) with its own DSN.
//
// Everything is gated on VITE_SENTRY_DSN: with no DSN (local dev, PR builds, or
// before the project exists) init is a no-op and captureException/captureMessage
// simply do nothing.

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const ENVIRONMENT = (import.meta.env.VITE_ENV as string | undefined) || 'development';
const RELEASE = import.meta.env.VITE_APP_VERSION as string | undefined;
// Optional first-party tunnel (a backend route forwarding envelopes) to dodge
// ad blockers. Unset → events go straight to Sentry.
const TUNNEL = import.meta.env.VITE_SENTRY_TUNNEL as string | undefined;

const TRACES_SAMPLE_RATE = ENVIRONMENT === 'production' ? 0.2 : 1.0;

export function initSentry(): void {
  if (!DSN) return;

  Sentry.init({
    dsn: DSN,
    environment: ENVIRONMENT,
    release: RELEASE,
    tunnel: TUNNEL || undefined,
    integrations: [Sentry.browserTracingIntegration()],
    // Known-benign noise that is never a real app bug.
    ignoreErrors: [
      /ResizeObserver loop (limit exceeded|completed)/,
      /Failed to fetch/i,
      /NetworkError when attempting to fetch resource/i,
      'Load failed',
      /AbortError/,
      'The operation was aborted.',
      'Non-Error promise rejection captured',
    ],
    denyUrls: [/^chrome-extension:\/\//, /^moz-extension:\/\//, /^safari-extension:\/\//],
    tracesSampleRate: TRACES_SAMPLE_RATE,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });

  // Load Session Replay lazily so its heavy bundle stays out of the entry chunk.
  void import('@sentry/react').then(({ replayIntegration, addIntegration }) => {
    addIntegration(replayIntegration({ maskAllText: true, blockAllMedia: true }));
  });
}

export function captureException(error: unknown, context?: Record<string, unknown>): void {
  if (!DSN) return;
  Sentry.captureException(error, context ? { extra: context } : undefined);
}
